import { ReactNode } from "react";
import { AppHeader } from "./AppHeader";

interface AppShellProps {
  children: ReactNode;
  /** Page title shown in header breadcrumb */
  title?: string;
  /** Optional subtitle/description */
  subtitle?: string;
  /** Optional actions (buttons) for page header */
  actions?: ReactNode;
  /** Whether to show the header (default: true) */
  showHeader?: boolean;
  /** Maximum width: 'default' | 'wide' | 'full' */
  maxWidth?: 'default' | 'wide' | 'full';
  /** Optional sidebar for complex pages (see Sidebar Usage Criteria) */
  sidebar?: ReactNode;
}

export function AppShell({ 
  children, 
  title,
  subtitle,
  actions,
  showHeader = true,
  maxWidth = 'default',
  sidebar
}: AppShellProps) {
  const maxWidthClass = {
    default: 'max-w-6xl',
    wide: 'max-w-7xl',
    full: 'max-w-full'
  }[maxWidth];

  // When sidebar is present, main content fills remaining space (no mx-auto centering)
  // When no sidebar, main content is centered with max-width
  const mainClassName = sidebar 
    ? 'flex-1 px-4 sm:px-6 lg:px-8 py-8 overflow-auto'
    : `${maxWidthClass} mx-auto px-4 sm:px-6 lg:px-8 py-8`;

  return (
    <div className="min-h-screen bg-background">
      {showHeader && <AppHeader />}
      
      <div className={sidebar ? 'flex h-[calc(100vh-4rem)]' : ''}>
        {/* Optional sidebar - fixed width, scrollable */}
        {sidebar}
        
        <main className={mainClassName}>
          {(title || actions) && (
            <div className="mb-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  {title && (
                    <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                      {title}
                    </h1>
                  )}
                  {subtitle && (
                    <p className="mt-1 text-muted-foreground">{subtitle}</p>
                  )}
                </div>
                {actions && (
                  <div className="flex items-center gap-3">
                    {actions}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {children}
        </main>
      </div>
    </div>
  );
}
